import {action, computed, makeObservable, observable} from 'mobx';

import type {Nullable} from '../../../utils/utility-types';
import type {CachedMediaFactory} from '../CachedMedia/CachedMedia.factory';

import {secondsToMinutesAndSeconds} from '../../../utils/datetime';
import {getUuid} from '../../../utils/getUuid';
import {logger} from '../../../utils/logger';
import {
  type Destructible,
  ReactionDisposeManager,
  type WithReactionDispose,
} from '../../../utils/store';
import {type CachedMediaModel} from '../CachedMedia/CachedMedia.model';

export abstract class BaseAudioModel
  implements Destructible, WithReactionDispose
{
  disposer = new ReactionDisposeManager();
  error: Nullable<Error> = null;
  id: string;
  abstract playbackRate: number;
  protected media: CachedMediaModel;
  protected PLAYBACK_RATES = [1, 1.5, 2];

  protected constructor(
    protected props: BaseAudioModel.Props,
    protected cachedMediaFactory: CachedMediaFactory,
  ) {
    this.media = cachedMediaFactory.create();

    this.id = props?.id ?? getUuid();
    makeObservable<BaseAudioModel, 'setError'>(this, {
      currentTimeSec: computed,
      durationSec: computed,
      error: observable,
      hasError: computed,
      playbackRateString: computed,
      renderTime: computed,
      setError: action.bound,
      togglePlay: action.bound,
      togglePlaybackRate: action.bound,
    });

    this.setupReactions();
  }

  async init({disableCache, src}: BaseAudioModel.InitProps) {
    await this.media.init({disableCache, ext: 'm4a', id: this.id, src});

    try {
      await this.initAudio();
    } catch (error) {
      this.setError(error);
      logger.error(error);
    }
  }

  destruct() {
    this.pause();
    this.disposer.dispose();
    this.media.destruct();
  }

  abstract get currentTime(): number;

  get currentTimeSec() {
    return Math.floor(this.currentTime);
  }

  abstract get duration(): number;

  get durationSec() {
    return Math.floor(this.duration);
  }

  get hasError() {
    return Boolean(this.error);
  }

  abstract get isEnded(): boolean;

  abstract get isLoaded(): boolean;

  abstract get isPaused(): boolean;

  get playbackRateString() {
    return `${this.playbackRate}x`;
  }

  get renderTime() {
    let time = this.isLoaded ? this.durationSec : 0;

    if (this.currentTimeSec !== 0 && this.currentTimeSec < this.durationSec) {
      time = this.currentTimeSec;
    } else if (this.isEnded) {
      time = this.durationSec;
    } else if (!this.isPaused) {
      time = this.currentTimeSec;
    }

    return secondsToMinutesAndSeconds(time);
  }

  abstract pause(): Promise<void>;

  abstract play(): Promise<void>;

  abstract seek(time: number): Promise<void>;

  abstract setPlaybackRate(rate: number): Promise<void>;

  async togglePlay() {
    if (this.isPaused || this.isEnded) {
      return this.play();
    } else {
      return this.pause();
    }
  }

  async togglePlaybackRate() {
    if (this.isLoaded) {
      const nextRate =
        this.PLAYBACK_RATES.find(rate => rate > this.playbackRate) ||
        this.PLAYBACK_RATES[0]!;

      return this.setPlaybackRate(nextRate);
    }
  }

  protected abstract initAudio(): Promise<void>;

  protected setError(error: Error) {
    this.error = error;
  }

  protected abstract setupReactions(): void;
}

export namespace BaseAudioModel {
  export interface InitProps {
    disableCache?: boolean;
    src: Nullable<string>;
  }

  export interface Props {
    id?: string;
    onEnded?: () => void;
    onPause?: () => void;
    onPlay?: () => void;
  }
}
